/** Reservation & offer queries. */

import { keepPreviousData, type UseQueryResult, useQuery } from '@tanstack/react-query';
import { queryKeys } from '../query-keys';
import { useApiClient } from '../react/ApiClientProvider';
import {
  type OfferDraft,
  offerDraftSchema,
  type Paginated,
  paginated,
  type Reservation,
  type ReservationStatus,
  reservationSchema,
} from '../responses';
import { unwrap } from './_helpers';

/** Server-side filter/search/pagination params for the reservations list. */
export type ReservationsFilters = {
  status?: ReservationStatus;
  urgency?: 'low' | 'medium' | 'high';
  contactLanguage?: string;
  /** Free-text match against id / room / board preference. */
  q?: string;
  /** Arrival on/after (YYYY-MM-DD). */
  from?: string;
  /** Arrival on/before (YYYY-MM-DD). */
  to?: string;
  limit?: number;
  offset?: number;
};

/** `GET /reservations` — filtered, searched, paginated reservation requests. */
export function useReservations(
  tenantId: string,
  hotelId: string,
  filters?: ReservationsFilters,
): UseQueryResult<Paginated<Reservation>> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.reservations(tenantId, hotelId, filters),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const result = await client.GET('/api/v1/tenants/{tenantId}/hotels/{hotelId}/reservations', {
        params: {
          path: { tenantId, hotelId },
          query: {
            status: filters?.status,
            urgency: filters?.urgency,
            contactLanguage: filters?.contactLanguage,
            q: filters?.q,
            from: filters?.from,
            to: filters?.to,
            limit: filters?.limit,
            offset: filters?.offset,
          },
        },
        parseAs: 'json',
      });
      return unwrap(result, paginated(reservationSchema));
    },
  });
}

/** `GET /reservations/:reservationId`. */
export function useReservation(
  tenantId: string,
  hotelId: string,
  reservationId: string,
): UseQueryResult<Reservation> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.reservation(tenantId, hotelId, reservationId),
    queryFn: async () => {
      const result = await client.GET(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/reservations/{reservationId}',
        { params: { path: { tenantId, hotelId, reservationId } }, parseAs: 'json' },
      );
      return unwrap(result, reservationSchema);
    },
    enabled: reservationId.length > 0,
  });
}

/** `GET /reservations/offers/:offerId`. */
export function useOffer(
  tenantId: string,
  hotelId: string,
  offerId: string,
): UseQueryResult<OfferDraft> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.reservationOffer(tenantId, hotelId, offerId),
    queryFn: async () => {
      const result = await client.GET(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/reservations/offers/{offerId}',
        { params: { path: { tenantId, hotelId, offerId } }, parseAs: 'json' },
      );
      return unwrap(result, offerDraftSchema);
    },
    enabled: offerId.length > 0,
  });
}
